/**
 * Programme alerts as Android notifications, inside the app (AlertsPlugin.java).
 * The app's WebView has no browser notifications, so Settings said the "browser"
 * did not support them and alerts could not be turned on (owner, 2026-10-09).
 * On the website these all answer null / do nothing, and the browser's own
 * notifications are used instead.
 */

interface CapacitorLike {
  isNativePlatform?: () => boolean;
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
  PluginHeaders?: Array<{ name: string }>;
}

const PLUGIN = "Alerts";

function capacitor(): CapacitorLike | null {
  const cap = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
  if (cap?.isNativePlatform?.() !== true || typeof cap.nativePromise !== "function") return null;
  if (!cap.PluginHeaders?.some((header) => header.name === PLUGIN)) return null;
  return cap;
}

/** True inside an app that can post Android notifications. */
export function nativeAlertsAvailable(): boolean {
  return capacitor() !== null;
}

/** Asks Android for permission when it would (13+, first time); "granted" or "denied". Null on the website. */
export async function requestNativeAlerts(): Promise<"granted" | "denied" | null> {
  const cap = capacitor();
  if (!cap) return null;
  try {
    const answer = (await cap.nativePromise!(PLUGIN, "request", {})) as { state?: string } | null;
    return answer?.state === "granted" ? "granted" : "denied";
  } catch {
    return "denied";
  }
}

/** One alert as an Android notification (a no-op when they are off). */
export function showNativeAlert(alert: { title: string; body: string; tag: string }): void {
  const cap = capacitor();
  if (!cap) return;
  cap.nativePromise!(PLUGIN, "show", alert).catch(() => {
    // The bell already has the alert.
  });
}
